import React from 'react'
import { useSelector } from 'react-redux'
import Row from '@amzn/meridian/row'
import Column from '@amzn/meridian/column'
import Text from '@amzn/meridian/text'
import Button from '@amzn/meridian/button'
import { formatRelativeTime } from '../utils'
import { IAggregatedNotificationItemProps } from '../types'
import { getSelectedLocale } from '@atredux/selectors/localeSelector'
import { VIEW_DETAILS, PENDING_TASKS } from '../constants'
import { useBundle } from '@amzn/react-arb-tools'
import { BUNDLE_NAME } from '@src/constants'
import { t } from '@src/translationHelper'
import { _navigateToTool } from '@components/SideBar/utils/SideBarUtil'
import { getToolNameToDetailMap } from '@services/ToolMenuConfigService'
import './AggregatedNotificationItem.scss'

/**
 * AggregatedNotificationItem Component
 *
 * Displays a summary of aggregated notifications for a store and tool.
 * Shows store name, notification details, and provides navigation to the tool.
 * Responsive design adapts to mobile, tablet, and desktop screens.
 */
export const AggregatedNotificationItem: React.FC<IAggregatedNotificationItemProps> = ({
    toolName,
    notificationMessage,
    numPendingTasks,
    message,
    timestamp,
}) => {
    const locale = useSelector(getSelectedLocale)
    const [bundle] = useBundle(BUNDLE_NAME)

    const handleViewDetails = () => {
        const tool = getToolNameToDetailMap().get(toolName)
        if (tool) {
            _navigateToTool(tool)
        }
    }

    const relativeTime = formatRelativeTime(timestamp, bundle, locale)

    return (
        <div className="aggregated-notification-item" data-testid="aggregated-notification-item">
            <Row alignmentVertical="top" spacing="300">
                <Column width="70%">
                    <div className="notification-content">
                        <Text type="h100">{notificationMessage}</Text>
                        <div className="pending-tasks">
                            <Text type="b200" color="inverted">{t(bundle, PENDING_TASKS, { pendingTaskCount: numPendingTasks })}</Text>
                        </div>
                        {message && <Text type="b200" color="secondary">{message.replace(/(\d+ fixtures?) /, '$1 · ')}</Text>}
                    </div>
                </Column>

                {/* TODO: Remove 'jwo-hidden' class once notifications are supported post-Pilot launch */}
                {/* https://app.asana.com/1/8442528107068/project/1210908264295868/task/1213923753699354?focus=true */}
                <Column width="30%">
                    <div className="notification-actions primary-theme-button jwo-hidden">
                        <Text type="b100" color="secondary">{relativeTime}</Text>
                        <Button
                            type="primary"
                            size="small"
                            onClick={handleViewDetails}
                            disabled={true}
                            data-testid="view-details-button"
                        >
                            {t(bundle, VIEW_DETAILS)}
                        </Button>
                    </div>
                </Column>
            </Row>
        </div>
    )
}
